import React, { useState } from "react";
import { ShieldCheck, Cookie, FileText, Ban, Check } from "lucide-react";
import SiteShell from "../components/site/SiteShell";
import useSEO from "../lib/useSEO";
import { api, formatApiError } from "../lib/api";

function LegalShell({ icon, kicker, title, updated, children, testId }) {
  return (
    <SiteShell>
      <section className="pt-32 pb-16 border-b border-white/5" data-testid={testId}>
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-5">
            <span className="text-[#d4af37]">{icon}</span>
            <div className="kicker">{kicker}</div>
          </div>
          <h1 className="h-display text-white text-[40px] sm:text-[56px] leading-[0.95]">{title}</h1>
          <p className="mt-4 text-white/40 text-[12px] tracking-[0.2em] uppercase">Last updated: {updated}</p>
        </div>
      </section>
      <section className="py-16">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 space-y-10">{children}</div>
      </section>
    </SiteShell>
  );
}

function Block({ heading, children }) {
  return (
    <div>
      <h2 className="text-white text-[16px] tracking-[0.18em] uppercase mb-4 border-l-2 border-[#d4af37] pl-4">{heading}</h2>
      <div className="text-white/65 text-[14px] leading-relaxed space-y-3">{children}</div>
    </div>
  );
}

const UPDATED = "June 2026";
const CONTACT_LINE = (
  <p>
    Questions? Contact us at{" "}
    <a href="mailto:info@raeenterprisellc.com" className="text-[#d4af37] hover:text-[#f2c66d]">info@raeenterprisellc.com</a>{" "}
    or 253-331-8487.
  </p>
);

export function PrivacyPolicyPage() {
  useSEO({ title: "Privacy Policy", description: "How Rae Enterprise LLC collects, uses and protects information.", path: "/privacy-policy" });
  return (
    <LegalShell icon={<ShieldCheck size={18} strokeWidth={1.3} />} kicker="Legal" title="PRIVACY POLICY." updated={UPDATED} testId="privacy-policy-page">
      <Block heading="Who We Are">
        <p>Rae Enterprise LLC ("we," "us") is a Seattle, Washington–based marketing firm specializing in geofencing and programmatic advertising. This policy explains what information we collect through this website and our advertising services, and how we use and protect it.</p>
      </Block>
      <Block heading="Information We Collect On This Website">
        <p>When you submit a form (contact, application, strategy call, or campaign builder), we collect the information you provide: name, email, phone, company, and campaign details. We also collect standard technical data such as browser type, pages visited, and referral source through cookies and similar technologies.</p>
      </Block>
      <Block heading="Advertising Data & Geofencing">
        <p>Our advertising campaigns use anonymized, aggregated mobile location data sourced from opt-in applications and licensed data providers. We never collect, access, or store names, phone numbers, personal accounts, or any personally identifiable information (PII) through geofencing. Device identifiers are encrypted and privacy-compliant, and targeting is based on behavioral patterns — not identity.</p>
      </Block>
      <Block heading="How We Use Information">
        <p>We use website information to respond to inquiries, prepare proposals, launch and manage campaigns you request, send confirmations, and improve our services. We do not sell your personal information.</p>
      </Block>
      <Block heading="Sharing">
        <p>We share information only with service providers necessary to operate our business (payment processing via Stripe, scheduling via Calendly, email delivery, advertising platforms used to run your campaigns) and when required by law. All providers are bound to protect your data.</p>
      </Block>
      <Block heading="Your Rights">
        <p>Depending on your state or country, you may have the right to access, correct, delete, or restrict the use of your personal information, and the right to opt out of the sale or sharing of personal information (see our <a href="/do-not-sell" className="text-[#d4af37] hover:text-[#f2c66d]">Do Not Sell or Share</a> page). We honor rights under the CCPA/CPRA, GDPR, and applicable U.S. state privacy laws.</p>
      </Block>
      <Block heading="Data Retention & Security">
        <p>We retain form submissions for as long as needed to serve you and meet legal obligations. Data is stored on secured, access-controlled infrastructure with encryption in transit.</p>
      </Block>
      <Block heading="Contact">{CONTACT_LINE}</Block>
    </LegalShell>
  );
}

export function TermsPage() {
  useSEO({ title: "Terms of Service", description: "Terms governing use of the Rae Enterprise LLC website and services.", path: "/terms-of-service" });
  return (
    <LegalShell icon={<FileText size={18} strokeWidth={1.3} />} kicker="Legal" title="TERMS OF SERVICE." updated={UPDATED} testId="terms-page">
      <Block heading="Acceptance">
        <p>By using this website or engaging Rae Enterprise LLC for advertising services, you agree to these terms. If you do not agree, please do not use the site.</p>
      </Block>
      <Block heading="Services">
        <p>We provide geofencing, programmatic display, video, and OTT/CTV advertising services. Campaign scope, pricing, impressions, and timelines are defined in your campaign brief or agreement. Performance figures shown in demos and case studies are illustrative or anonymized and do not guarantee specific results.</p>
      </Block>
      <Block heading="Payment">
        <p>Campaign fees are due as invoiced. Payments are processed securely through Stripe. Campaigns launch after payment confirmation and brief approval. Media minimums apply per format.</p>
      </Block>
      <Block heading="Compliant Advertising">
        <p>We follow platform policies and applicable law, including special handling for sensitive categories (healthcare, legal, political, gaming). We may decline campaigns that violate law or platform policy.</p>
      </Block>
      <Block heading="Intellectual Property">
        <p>All site content, branding, and creative produced by Rae Enterprise LLC remains our property until paid in full, after which campaign creative produced for you transfers to you. You may not reproduce site content without permission.</p>
      </Block>
      <Block heading="Limitation of Liability">
        <p>To the fullest extent permitted by law, Rae Enterprise LLC is not liable for indirect, incidental, or consequential damages arising from use of the site or services. Our total liability is limited to fees paid for the applicable campaign.</p>
      </Block>
      <Block heading="Governing Law">
        <p>These terms are governed by the laws of the State of Washington. Disputes will be resolved in the courts of King County, Washington.</p>
      </Block>
      <Block heading="Contact">{CONTACT_LINE}</Block>
    </LegalShell>
  );
}

export function CookiePolicyPage() {
  useSEO({ title: "Cookie Policy", description: "How Rae Enterprise LLC uses cookies and similar technologies.", path: "/cookie-policy" });
  return (
    <LegalShell icon={<Cookie size={18} strokeWidth={1.3} />} kicker="Legal" title="COOKIE POLICY." updated={UPDATED} testId="cookie-policy-page">
      <Block heading="What Are Cookies">
        <p>Cookies are small text files stored on your device that help websites function and understand how they are used. We also use similar technologies such as local storage and pixels.</p>
      </Block>
      <Block heading="Cookies We Use">
        <p><span className="text-white">Essential</span> — required for the site to work (session preferences, security). These cannot be disabled.</p>
        <p><span className="text-white">Functional</span> — remember choices such as dismissing our welcome popup (stored in your browser's session storage).</p>
        <p><span className="text-white">Analytics & Advertising</span> — help us understand site traffic and measure ad performance. Third parties such as Calendly (scheduling) and Stripe (payments) may set their own cookies when you use their embedded features.</p>
      </Block>
      <Block heading="Managing Cookies">
        <p>You can control or delete cookies through your browser settings at any time. Blocking essential cookies may affect how the site functions. To opt out of interest-based advertising broadly, visit <a href="https://optout.aboutads.info" target="_blank" rel="noopener noreferrer" className="text-[#d4af37] hover:text-[#f2c66d]">optout.aboutads.info</a>.</p>
      </Block>
      <Block heading="Contact">{CONTACT_LINE}</Block>
    </LegalShell>
  );
}

export function DoNotSellPage() {
  useSEO({ title: "Do Not Sell or Share My Personal Information", description: "Submit a do-not-sell request under CCPA/CPRA to Rae Enterprise LLC.", path: "/do-not-sell" });
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        message: `[DO NOT SELL / SHARE REQUEST] ${form.message || "Please do not sell or share my personal information."}`,
      });
      setStatus("done");
    } catch (err) {
      setStatus("idle");
      setError(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <LegalShell icon={<Ban size={18} strokeWidth={1.3} />} kicker="Your Privacy Rights" title="DO NOT SELL OR SHARE." updated={UPDATED} testId="do-not-sell-page">
      <Block heading="Your Right To Opt Out">
        <p>Under the California Consumer Privacy Act (CCPA/CPRA) and similar U.S. state privacy laws, you have the right to direct us not to sell or share your personal information. Rae Enterprise LLC does not sell personal information — but we honor every request and will confirm in writing.</p>
      </Block>
      <Block heading="Submit A Request">
        {status === "done" ? (
          <div className="border border-[#d4af37]/40 bg-[#0d0d0d] p-8 flex items-start gap-4" data-testid="dns-success">
            <Check size={18} strokeWidth={2} className="text-[#d4af37] mt-0.5" />
            <p>Your request has been received. We will confirm within 15 business days at the email you provided.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5 max-w-[520px]" data-testid="dns-form">
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-white/50 mb-2">Full Name</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full bg-[#0d0d0d] border border-white/15 focus:border-[#d4af37] outline-none px-4 py-3 text-white text-[14px] transition-colors"
                data-testid="dns-name-input"
              />
            </div>
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-white/50 mb-2">Email</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full bg-[#0d0d0d] border border-white/15 focus:border-[#d4af37] outline-none px-4 py-3 text-white text-[14px] transition-colors"
                data-testid="dns-email-input"
              />
            </div>
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-white/50 mb-2">Details (Optional)</label>
              <textarea
                rows={3}
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                className="w-full bg-[#0d0d0d] border border-white/15 focus:border-[#d4af37] outline-none px-4 py-3 text-white text-[14px] transition-colors"
                data-testid="dns-message-input"
              />
            </div>
            {error && <p className="text-red-400 text-[13px]" data-testid="dns-error">{error}</p>}
            <button type="submit" disabled={status === "loading"} className="btn-gold btn-label disabled:opacity-60" data-testid="dns-submit-btn">
              {status === "loading" ? "Submitting..." : "Submit Request"}
            </button>
          </form>
        )}
      </Block>
      <Block heading="Other Ways To Reach Us">{CONTACT_LINE}</Block>
    </LegalShell>
  );
}
